/**
 * The sign-in gate.
 *
 * Every screen sits behind this. It asks the API who the current session
 * belongs to; while that is unknown it renders a loading state, because
 * flashing the application and then replacing it with a login form is worse
 * than a brief wait.
 *
 * There is no token in this component and no token anywhere in the frontend.
 * The session is an HttpOnly cookie the browser attaches on its own, so the
 * property that matters holds: an XSS here cannot steal a credential, because
 * JavaScript never has one. No `sessionStorage`, no `document.cookie`.
 *
 * One exception, and it is written down rather than glossed: with "beni
 * hatırla" ticked, the *email address* is kept in `localStorage` so the field
 * arrives filled - see `rememberedEmail.ts` for what that does and does not
 * cover. No password and no token is ever stored there.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  getCurrentUser,
  getRegistrationStatus,
  login as loginRequest,
  logout as logoutRequest,
  register as registerRequest,
  requestPasswordReset,
  verifyPasswordReset,
  completePasswordReset,
} from "@/api/client";
import { forgetAllMeshes } from "@/features/reconstruction/meshCache";
import { ApiError } from "@/api/errors";

/**
 * How long to keep confirming a sign-in the server already accepted, in
 * milliseconds between attempts. Six attempts, 11.2 seconds in total.
 *
 * The number is set by measurement, not taste. The old schedule was three
 * attempts inside 600 ms, and on the development machine a correct sign-in
 * needed longer than that four times out of six - the longest observed run
 * took about 6.6 seconds before `/auth/me` answered 200. The window is roughly
 * double that, because the cost of waiting an extra second is a spinner and
 * the cost of giving up too early is telling somebody their password worked
 * but their session did not.
 */
const CONFIRMATION_DELAYS_MS = [200, 400, 800, 1600, 3200, 5000] as const;
import type { SessionUser } from "@/api/types";
import { BrandMark, BrandWordmark, IconEye, IconEyeOff } from "@/components/icons";
import { AuthBackdrop } from "./AuthBackdrop";
import { forgetEmail, readRememberedEmail, rememberEmail } from "./rememberedEmail";
import { Field } from "@/components/Field";
import { Button, Loading } from "@/components/ui";
import { useAsync } from "@/lib/useAsync";
import "./auth.css";
import { LOGIN_STORIES } from "./loginStories";

interface AuthValue {
  user: SessionUser;
  signOut: () => Promise<void>;
  /**
   * Re-read `/auth/me`.
   *
   * Exists because the display name is now editable, and the greeting on Ana
   * Sayfa reads it from this context. Without a way to refresh, renaming
   * yourself would leave the old name on screen until the next sign-in - which
   * looks exactly like a save that did not work.
   */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

/** Mirrors the server's policy so the form can say the rule before it is broken. */
const PASSWORD_MIN_LENGTH = 8;

function PasswordResetPanel({ initialEmail, onBack }: { initialEmail: string; onBack: () => void }) {
  const [step, setStep] = useState<"email" | "code" | "password" | "done">("email");
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function advance() {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      if (step === "email") {
        await requestPasswordReset(email);
        setStep("code");
      } else if (step === "code") {
        const result = await verifyPasswordReset({ email, code });
        setResetToken(result.reset_token);
        setStep("password");
      } else if (step === "password") {
        if (newPassword !== confirmPassword) {
          setFailure("Parolalar eşleşmiyor.");
          return;
        }
        await completePasswordReset({ reset_token: resetToken, new_password: newPassword });
        setStep("done");
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        setFailure("Çok fazla deneme yapıldı. Lütfen bir süre sonra tekrar deneyin.");
      } else if (step === "code") {
        setFailure("Kod geçersiz veya süresi dolmuş.");
      } else if (step === "password") {
        // 422 is the password policy; 400 is the reset itself having expired or
        // already been used. Saying "too short" for the second one sends
        // somebody off to invent a longer password when what they actually
        // need is a new code - the screen would be stating a cause it has not
        // been told.
        setFailure(
          error instanceof ApiError && error.status === 400
            ? "Yenileme oturumunuz sona erdi. Lütfen yeniden kod isteyin."
            : `Parola en az ${PASSWORD_MIN_LENGTH} karakter olmalıdır.`,
        );
      } else {
        setFailure("Kod gönderilemedi. Lütfen tekrar deneyin.");
      }
    } finally {
      setBusy(false);
    }
  }

  if (step === "done") {
    return (
      <div className="auth__reset-done" role="status">
        <span aria-hidden="true">✓</span>
        <strong>Parolanız yenilendi</strong>
        <p>Yeni parolanızla güvenle giriş yapabilirsiniz.</p>
        <Button variant="primary" type="button" onClick={onBack}>Giriş ekranına dön</Button>
      </div>
    );
  }

  return (
    <div className="auth__reset">
      <button type="button" className="auth__reset-back" onClick={onBack}>← Girişe dön</button>
      <div className="auth__reset-heading">
        <strong>{step === "email" ? "Parolanızı yenileyin" : step === "code" ? "E-postanızı kontrol edin" : "Yeni parola oluşturun"}</strong>
        <span>{step === "email" ? "Hesabınıza bağlı e-posta adresini yazın." : step === "code" ? `${email} adresine gönderilen 6 haneli kodu girin.` : "Güçlü ve daha önce kullanmadığınız bir parola seçin."}</span>
      </div>
      <div className="auth__reset-steps" aria-label="Parola yenileme adımları">
        {["email", "code", "password"].map((item, index) => (
          <i key={item} className={item === step ? "active" : index < ["email", "code", "password"].indexOf(step) ? "done" : ""} />
        ))}
      </div>
      {step === "email" && <Field label="E-posta" required>{(props) => <input {...props} type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} autoFocus />}</Field>}
      {step === "code" && <Field label="Doğrulama kodu" required>{(props) => <input {...props} className="auth__code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))} autoFocus />}</Field>}
      {step === "password" && <>
        <Field label="Yeni parola" required>{(props) => <input {...props} type="password" autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} autoFocus />}</Field>
        <Field label="Yeni parola tekrar" required>{(props) => <input {...props} type="password" autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />}</Field>
      </>}
      {failure && <p className="auth__error" role="alert">{failure}</p>}
      <Button variant="primary" type="button" busy={busy} onClick={() => void advance()} disabled={step === "email" ? !email.includes("@") : step === "code" ? code.length !== 6 : newPassword.length < PASSWORD_MIN_LENGTH || confirmPassword.length < PASSWORD_MIN_LENGTH}>
        {step === "email" ? "Kod gönder" : step === "code" ? "Kodu doğrula" : "Parolayı yenile"}
      </Button>
      {step === "code" && <button type="button" className="auth__help-link auth__resend" onClick={() => setStep("email")}>Kodu yeniden gönder</button>}
    </div>
  );
}

/**
 * One place to turn a failure into a sentence.
 *
 * The login path stays uniform - wrong password and unknown address read the
 * same, matching the server, so the form is not an account oracle. Sign-up
 * necessarily says when an address is taken, because the alternative is
 * failing to create the account without explaining why.
 */
function messageFor(error: unknown, mode: "login" | "register"): string {
  if (!(error instanceof ApiError)) {
    return "Sunucuya ulaşılamıyor. Lütfen tekrar deneyin.";
  }
  // The request never reached the server: a different problem from anything
  // the server could have said, and a different thing to try next.
  if (error.kind === "network" || error.kind === "timeout") {
    return "Sunucuya ulaşılamıyor. Lütfen tekrar deneyin.";
  }
  if (mode === "register") {
    if (error.status === 409) return "Bu e-posta adresiyle zaten bir hesap var.";
    if (error.status === 422)
      return `Parola en az ${PASSWORD_MIN_LENGTH} karakter olmalıdır.`;
    if (error.status === 403) return "Bu sunucuda yeni hesap oluşturulamıyor.";
    // 404 means this server has no registration route at all - an older
    // deployment. Say that, rather than a generic failure that sends someone
    // looking at their password.
    if (error.status === 404)
      return "Bu sunucu sürümü hesap oluşturmayı desteklemiyor. Sunucunun güncellenmesi gerekiyor.";
    return "Hesap oluşturulamadı. Lütfen tekrar deneyin.";
  }
  return error.status === 401
    ? "E-posta veya parola hatalı."
    : "Giriş yapılamadı. Lütfen tekrar deneyin.";
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth used outside AuthGate");
  return value;
}

/**
 * Who is signed in, or `null` outside the gate.
 *
 * For code that wants the identity but must not *require* it - the model
 * viewer keys its parsed-mesh cache on the user id, and a viewer rendered
 * without the gate should lose its cache rather than crash. `useAuth` still
 * throws, because a screen that genuinely needs an account is better broken
 * loudly than rendered signed-out.
 *
 * Failing to `null` is the safe direction here: `meshCache` stores nothing
 * without an identity, so the worst outcome is a parse that happens again.
 */
export function useOptionalAuthUser(): SessionUser | null {
  return useContext(AuthContext)?.user ?? null;
}

function LoginScreen({
  onSignedIn,
  onAttempt,
  sessionDropped = false,
}: {
  onSignedIn: (user: SessionUser) => void;
  /** Fired when a new attempt starts, so stale notices can be cleared. */
  onAttempt?: () => void;
  /** The server accepted the credentials and the session still did not stick. */
  sessionDropped?: boolean;
}) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [storyIndex,setStoryIndex]=useState(0);
  const story=LOGIN_STORIES[storyIndex]!;
  // Pre-filled from the last remembered sign-in, so returning means typing a
  // password rather than an address as well. Read once, on mount.
  const [email, setEmail] = useState(() => readRememberedEmail() ?? "");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const passwordConfirmRef = useRef<HTMLInputElement>(null);
  const [displayName, setDisplayName] = useState("");
  const [revealed, setRevealed] = useState(false);
  // Off by default, deliberately: a shared machine should have to be told to
  // remember rather than told to forget. Ticked on arrival only when the last
  // sign-in from this browser asked to be remembered - otherwise the box would
  // claim a setting nobody chose.
  const [remember, setRemember] = useState(() => readRememberedEmail() !== null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [capsLock, setCapsLock] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  function updateCapsLock(event: KeyboardEvent<HTMLInputElement>) {
    setCapsLock(event.getModifierState("CapsLock"));
  }

  // The sign-up tab is hidden only when the server *says* registration is
  // closed. Anything else - still asking, request failed, route not there -
  // shows it.
  //
  // The first version hid it whenever the check did not succeed, and that is
  // how it vanished on the domain deployment: an older API answered 404 to
  // /auth/registration, the check failed, and the interface concluded "no
  // sign-up here". Failing to ask a question is not the same as being told no.
  // If the server really does refuse, the attempt returns 403 and says so in
  // words, which is a better failure than a feature that quietly disappears.
  const statusFetcher = useCallback(
    (signal: AbortSignal) => getRegistrationStatus({ signal }),
    [],
  );
  const { state: registration } = useAsync(statusFetcher);
  const registrationClosed =
    registration.status === "success" && !registration.data.open;
  const canRegister = !registrationClosed;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setFailure(null);
    if (mode === "register" && password.length < PASSWORD_MIN_LENGTH) {
      setFailure(`Parola en az ${PASSWORD_MIN_LENGTH} karakter olmalıdır.`);
      setBusy(false);
      return;
    }
    if (mode === "register" && password !== passwordConfirm) {
      // Not repeated at form level. The field under the input already carries
      // this sentence, with `role="alert"`, and setting it twice announced the
      // same problem twice and put one of the two copies far from the input it
      // is about. Moving focus is what turns the existing message into an
      // answer to the press.
      passwordConfirmRef.current?.focus();
      setBusy(false);
      return;
    }
    // A new attempt supersedes whatever the last one concluded, including the
    // gate's dropped-session notice - otherwise it lingers next to a fresh
    // error and the screen shows two contradictory explanations.
    onAttempt?.();
    try {
      let signedIn;
      if (mode === "register") {
        signedIn = await registerRequest({ email, password, display_name: displayName });
      } else {
        signedIn = await loginRequest({ email, password, remember });
        // Only after the server accepted it. Remembering an address that
        // failed to sign in would helpfully pre-fill a typo forever.
        if (remember) {
          rememberEmail(email);
        } else {
          forgetEmail();
        }
      }
      onSignedIn(signedIn);
      // Released even on the happy path. When the session sticks this
      // component unmounts and it does not matter; when it does not stick,
      // leaving the button disabled is what makes the screen look frozen with
      // nothing to read and no way to retry.
      setBusy(false);
    } catch (error) {
      setFailure(messageFor(error, mode));
      setBusy(false);
    }
  }

  return (
    <main className="auth">
      {/* A sibling of the form, never a parent: the backdrop cannot swallow a
          click, take focus, or take the login down with it if it fails. */}
      <AuthBackdrop />
      <div className="auth__shell">
        <section className="auth__story" aria-labelledby="auth-story-title">
          {/*
            The controls sit outside the keyed frame on purpose. `key` remounts
            the frame on every change, and a button that unmounts under the
            pointer takes the keyboard focus with it - pressing "next" once
            would drop focus to the body and the second press would go nowhere.
          */}
          <div className="auth__story-inner">
            <div className="auth__story-frame" key={storyIndex}>
              <div className="auth__eyebrow"><span aria-hidden="true" /> {story.eyebrow}</div>
              <h1 id="auth-story-title">{story.headline}</h1>
              <p>{story.description}</p>
              <div className="auth__capabilities" aria-label="Platform özellikleri">{story.chips.map((chip)=><span key={chip}>{chip}</span>)}</div>
            </div>
            <div className="auth__story-controls">
              <button type="button" onClick={() => setStoryIndex((storyIndex - 1 + LOGIN_STORIES.length) % LOGIN_STORIES.length)} aria-label="Önceki tanıtım">←</button>
              {/* A real group, so the label is exposed: `aria-label` on a bare
                  div names nothing. */}
              <div className="auth__story-dots" role="group" aria-label="Tanıtım slaytları">
                {LOGIN_STORIES.map((item, index) => (
                  <button key={item.headline} type="button" aria-label={`${index + 1}. tanıtımı göster`} aria-current={index === storyIndex ? "true" : undefined} onClick={() => setStoryIndex(index)} />
                ))}
              </div>
              <button type="button" onClick={() => setStoryIndex((storyIndex + 1) % LOGIN_STORIES.length)} aria-label="Sonraki tanıtım">→</button>
            </div>
            {/*
              The slide no longer changes by itself, so nothing announces it
              either. This is the one thing a screen reader cannot get from the
              dots, whose state is visual.
            */}
            <p className="auth__story-status visually-hidden" role="status">
              {`${storyIndex + 1} / ${LOGIN_STORIES.length}: ${story.headline}`}
            </p>
          </div>
        </section>

        <form className="auth__card" onSubmit={submit}>
        <div className="auth__brand">
          <BrandMark size={34} />
          <BrandWordmark />
        </div>
        {/*
          Below 520px the story panel is display:none, and it carries the only
          sentence that says what the product makes. Without this the phone
          sign-in screen is a form with a logo above it. Shown at that width
          only, so desktop does not say the same thing twice.
        */}
        <p className="auth__tagline">
          Fotoğraflarınızı çekin veya yükleyin; SentyCap bunlardan 3D model
          oluşturmanıza yardımcı olur.
        </p>
        <div className="auth__welcome">
          <strong>{mode === "login" ? "Tekrar hoş geldiniz" : "Hesabınızı oluşturun"}</strong>
          <span>{mode === "login" ? "Devam etmek için hesabınıza giriş yapın." : "İlk projenize birkaç saniye içinde başlayın."}</span>
        </div>
        {resetOpen ? (
          <PasswordResetPanel initialEmail={email} onBack={() => setResetOpen(false)} />
        ) : (
          <>
        {canRegister ? (
          <div className="auth__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "login"}
              className={`auth__tab${mode === "login" ? " auth__tab--active" : ""}`}
              onClick={() => {
                setMode("login");
                setFailure(null);
              }}
            >
              Giriş Yap
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "register"}
              className={`auth__tab${mode === "register" ? " auth__tab--active" : ""}`}
              onClick={() => {
                setMode("register");
                setFailure(null);
              }}
            >
              Kayıt Ol
            </button>
          </div>
        ) : (
          <h1>Giriş Yap</h1>
        )}

        {mode === "register" && (
          <Field label="Ad Soyad" required>
            {(props) => (
              <input
                {...props}
                autoComplete="name"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
              />
            )}
          </Field>
        )}

        <Field label="E-posta" required>
          {(props) => (
            <input
              {...props}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoFocus
            />
          )}
        </Field>
        <Field label="Parola" required>
          {(props) => (
            // The reveal toggle flips the input's type rather than rendering
            // the password into text somewhere else, so the value still lives
            // in exactly one place and password managers keep working.
            <div className="auth__password">
              <input
                {...props}
                type={revealed ? "text" : "password"}
                autoComplete={mode === "register" ? "new-password" : "current-password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                onKeyDown={updateCapsLock}
                onKeyUp={updateCapsLock}
                onBlur={() => setCapsLock(false)}
              />
              <button
                type="button"
                className="auth__reveal"
                onClick={() => setRevealed(!revealed)}
                aria-pressed={revealed}
                aria-label={revealed ? "Parolayı gizle" : "Parolayı göster"}
                title={revealed ? "Parolayı gizle" : "Parolayı göster"}
              >
                {/* The icon shows the current state - an open eye while the
                    password is on screen, a struck-through one while it is
                    hidden. The label carries the action, so the button still
                    announces what a click will do. */}
                {revealed ? <IconEye size={18} /> : <IconEyeOff size={18} />}
              </button>
            </div>
          )}
        </Field>
        {mode === "register" && (
          <Field label="Parola tekrar" required error={passwordConfirm && password !== passwordConfirm ? "Parolalar eşleşmiyor." : null}>
            {(props) => (
              <input {...props} ref={passwordConfirmRef} type={revealed ? "text" : "password"} autoComplete="new-password" value={passwordConfirm} onChange={(event) => setPasswordConfirm(event.target.value)} />
            )}
          </Field>
        )}
        {capsLock && (
          <p className="auth__caps" role="status">
            <span aria-hidden="true">⇧</span> Caps Lock açık
          </p>
        )}

        {/* Sign-in only. On the sign-up tab it would be a promise about a
            session that has not been opened yet, and the account that has
            just been created signs in for this browsing session only. */}
        {mode === "login" && (
          <div className="auth__options">
            <label className="auth__remember">
              <input
                type="checkbox"
                checked={remember}
                onChange={(event) => setRemember(event.target.checked)}
              />
              <span>Beni hatırla</span>
            </label>
            <button
              type="button"
              className="auth__help-link"
              onClick={() => setResetOpen(true)}
            >
              Parolamı unuttum
            </button>
          </div>
        )}

        {failure && (
          <p className="auth__error" role="alert">
            {failure}
          </p>
        )}

        {/* The case that cost a real debugging session: login answers 200,
            `/auth/me` answers 401, and the form comes back empty with nothing
            to read. Silence there looks like "nothing happened" when what
            actually happened is that the browser threw the session away. */}
        {sessionDropped && !failure && (
          <p className="auth__error" role="alert">
            Giriş bilgileri kabul edildi, ancak oturum tarayıcıda saklanmadı.
            Bu genellikle arayüzün API'yi farklı bir adresten çağırmasından
            kaynaklanır; oturum çerezi adresler arasında taşınmaz.
          </p>
        )}

        {mode === "register" && (
          <p className="auth__note">
            Parola en az {PASSWORD_MIN_LENGTH} karakter olmalıdır. {password.length === 0 ? "" : password.length < PASSWORD_MIN_LENGTH ? "Henüz çok kısa." : "Uzunluk kuralı karşılandı."}
          </p>
        )}

        {/*
          Never disabled. A greyed-out primary button states that something is
          wrong without saying what, and it cannot be focused to find out; the
          submit handler rejects the same two cases and names them instead.
        */}
        <Button variant="primary" type="submit" busy={busy}>
          {mode === "register" ? "Hesap Oluştur" : "Giriş Yap"}
        </Button>

        <div className="auth__trust" aria-label="Güvenlik bilgisi">
          <span aria-hidden="true" className="auth__trust-mark">✓</span>
          <span>Şifreli oturum</span><i aria-hidden="true" />
          <span>İzole proje alanı</span><i aria-hidden="true" />
          <span>Güvenli medya aktarımı</span>
        </div>

        {registrationClosed && (
          <p className="auth__note">
            Bu sunucuda yeni hesap oluşturma kapalı. Hesabınız yoksa sistem
            yöneticinizden bir hesap oluşturmasını isteyin.
          </p>
        )}
          </>
        )}
        </form>
      </div>
    </main>
  );
}

export function AuthGate({
  children,
  onAuthenticated,
  confirmationDelaysMs = CONFIRMATION_DELAYS_MS,
}: {
  children: ReactNode;
  /**
   * The confirmation schedule, overridable so a test can exercise the whole
   * window without eleven seconds of real time. Production never passes it.
   */
  confirmationDelaysMs?: readonly number[];
  /**
   * Called once each time a **sign-in attempt made in this tab** succeeds.
   *
   * The product rule it exists for: authentication has one destination, and
   * that destination is Ana Sayfa. Not the project someone was looking at
   * when their session expired, not the reconstruction route they had
   * bookmarked - Home, every time.
   *
   * It is a prop rather than a `useNavigate` inside this component because
   * the gate is mounted *outside* `RouterProvider`: nothing renders until the
   * API says who the session belongs to, so there is no router above it to
   * navigate with. `main.tsx` supplies the one line that drives the router.
   *
   * It deliberately does **not** fire when an existing session is recognised
   * on page load. That case is someone opening a deep link they are already
   * entitled to, and sending them Home would break every bookmark in the
   * product to solve a problem nobody has.
   */
  onAuthenticated?: () => void;
}) {
  const fetcher = useCallback((signal: AbortSignal) => getCurrentUser({ signal }), []);
  // `reload` rather than a changing dependency key: the request is identical
  // before and after signing in, only the answer differs.
  const { state, reload } = useAsync(fetcher);
  const [sessionDropped, setSessionDropped] = useState(false);
  //: Whether a sign-in the server accepted is still being confirmed.
  //:
  //: The old code confirmed for 600 ms - three attempts, 200 ms apart - and
  //: then told the customer their session had been thrown away. Measured on
  //: the development machine over six real browser sign-ins with correct
  //: credentials, **four ended on "giriş bilgileri kabul edildi, ancak oturum
  //: tarayıcıda saklanmadı"**. Nothing had been thrown away: on this hardware
  //: Chromium takes seconds to make a freshly issued cookie available to the
  //: page's own requests, and `/auth/me` answered 200 as soon as it did. The
  //: server side was never in doubt - login followed by `/auth/me` with the
  //: cookie it had just issued answered 200 twenty times out of twenty,
  //: including twenty fired concurrently.
  //:
  //: So the window is now long enough to cover it, and what the customer sees
  //: during it is "signing in" rather than a failure. Waiting is honest;
  //: announcing a failure that has not happened is not.
  //:
  //: **The wait cannot be skipped by trusting the login response.** That was
  //: tried: the response carries the same `SessionUser` as `/auth/me`, so
  //: opening the application on it looks free. It is not - during the same
  //: window `/projects`, `/workspace/summary`, `/tasks` and
  //: `/notifications/unread-count` all answer 401 too, and the customer lands
  //: on a home screen that says "Beklenmeyen bir hata oluştu". A session that
  //: cannot be used yet is not a session to let somebody in on.
  const [signingIn, setSigningIn] = useState(false);
  //: How many confirmations are still owed.
  const [attemptsLeft, setAttemptsLeft] = useState(0);
  // Keyed on `signingIn`, not on the attempt counter. Keying on the counter
  // meant the effect stopped running at exactly the moment the last attempt
  // was spent, so the branch that gives up could never be reached and the
  // spinner ran forever. Caught by the test for that branch.

  const handleSignedIn = useCallback(() => {
    setSigningIn(true);
    setAttemptsLeft(confirmationDelaysMs.length);
    setSessionDropped(false);
    reload();
  }, [reload, confirmationDelaysMs]);

  // Whether the session survived the round trip is only knowable here, from
  // the answer to the request that follows a sign-in.
  //
  // The dependency is the whole `state` object, not `state.status`. Keying on
  // the status string looks tidier and is wrong: the status before a sign-in
  // and after a rejected re-check are both "error", so the effect would never
  // fire in exactly the case it exists for. `useAsync` allocates a new state
  // object per transition, so identity is the honest trigger.
  useEffect(() => {
    if (!signingIn) return;
    if (state.status === "success") {
      setAttemptsLeft(0);
      setSigningIn(false);
      // The single place authentication succeeds after an attempt, which is
      // why the redirect lives here rather than in the form's submit handler:
      // registration, ordinary login and the remembered-address path all pass
      // through `handleSignedIn` and end here, and a redirect fired from the
      // submit handler would also fire for a sign-in the browser then threw
      // away.
      onAuthenticated?.();
      return;
    }
    if (state.status !== "error") return;
    if (attemptsLeft > 0) {
      // Backoff rather than a fixed 200 ms. The old schedule gave up after
      // three attempts inside two thirds of a second, which is shorter than
      // the cookie commit took on four of six measured sign-ins.
      const delay = confirmationDelaysMs[confirmationDelaysMs.length - attemptsLeft]
        ?? confirmationDelaysMs[confirmationDelaysMs.length - 1];
      const retry = window.setTimeout(() => {
        setAttemptsLeft((left) => left - 1);
        reload();
      }, delay);
      return () => window.clearTimeout(retry);
    }
    // The whole window elapsed and the session is still unusable. Now the
    // message is earned: something really is discarding the cookie, and the
    // cross-origin API it names is the way that happens.
    setAttemptsLeft(0);
    setSigningIn(false);
    setSessionDropped(true);
    return;
  }, [signingIn, attemptsLeft, state, reload, onAuthenticated, confirmationDelaysMs]);

  const signOut = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      // Anything this account's screens kept in memory goes with the session.
      // The mesh cache already keys on the user id, so a second account could
      // not read the first one's model in any case; this is the other half of
      // that - a signed-out person's geometry must not sit in a shared
      // browser's memory waiting for the tab to be closed. In `finally`
      // because a logout request that failed still means the person left.
      forgetAllMeshes();
      setAttemptsLeft(0);
      setSigningIn(false);
      setSessionDropped(false);
      reload();
    }
  }, [reload]);

  const user = state.status === "success" ? state.data : null;

  // A sign-in the server accepted, not yet usable by this browser. The wait is
  // named for what it is; the login form must not come back underneath it,
  // because that reads as "it did not work" and invites a second attempt that
  // races the first.
  if (!user && signingIn) {
    return <Loading label="Oturum açılıyor" />;
  }
  if (!user && (state.status === "idle" || (state.status === "loading" && state.data === null))) {
    return <Loading label="Oturum denetleniyor" />;
  }
  if (!user) {
    // Any failure to identify the session - 401, or the API being unreachable -
    // lands on the login form rather than on a broken application.
    return (
      <LoginScreen
        onSignedIn={handleSignedIn}
        onAttempt={() => setSessionDropped(false)}
        sessionDropped={sessionDropped}
      />
    );
  }
  return (
    <AuthContext.Provider value={{ user, signOut, refreshUser: async () => reload() }}>
      {children}
    </AuthContext.Provider>
  );
}
